import React, { useState } from 'react';

import { MoreVertical } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface ActionLinkConfig {
  label: string;
  onClick: () => void;
  className?: string;
}

interface ResponsiveActionLinksProps {
  actions: ActionLinkConfig[];
  className?: string;
}

/**
 * Responsive action links component that displays buttons with separators.
 * On larger screens (md+), shows as inline buttons separated by '|'.
 * On smaller screens, shows a dropdown menu with three dots.
 */
export function ResponsiveActionLinks({ actions, className }: ResponsiveActionLinksProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleAction = (onClick: () => void) => {
    onClick();
    setIsDropdownOpen(false);
  };

  return (
    <>
      {/* Desktop buttons - single row on xl screens */}
      <div className={cn('hidden items-center gap-2 xl:flex', className)}>
        {actions.map((action, index) => (
          <React.Fragment key={index}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={action.onClick}
              className={cn(
                'h-auto px-2 py-1 text-sm font-normal text-foreground hover:text-foreground/80 whitespace-nowrap',
                action.className
              )}
            >
              {action.label}
            </Button>
            {index < actions.length - 1 && (
              <span className="text-sm text-muted-foreground">{`|`}</span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Desktop buttons - 3 rows on large screens */}
      <div className={cn('hidden lg:flex xl:hidden', className)}>
        <div className="flex flex-col items-start gap-1">
          {actions.map((action, index) => (
            <Button
              type="button"
              key={index}
              variant="ghost"
              size="sm"
              onClick={action.onClick}
              className={cn(
                'h-auto px-2 py-1 text-sm font-normal text-foreground hover:text-foreground/80 whitespace-nowrap',
                action.className
              )}
            >
              {action.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Mobile dropdown - show on medium screens and below */}
      <div className="lg:hidden">
        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 w-9 p-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {actions.map((action, index) => (
              <div key={index}>
                <DropdownMenuItem onClick={() => handleAction(action.onClick)}>
                  {action.label}
                </DropdownMenuItem>
                {index < actions.length - 1 && <DropdownMenuSeparator />}
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
